(() => {
  const SELECTORS = {
    root: '[data-product-bundle]',
    card: '[data-bundle-card]',
    select: '[data-option-position]',
    checkbox: '[data-bundle-select]',
    toggle: '[data-bundle-toggle]'
  };

  function parseJSON(node) {
    try { return node ? JSON.parse(node.textContent) : []; } catch (error) { return []; }
  }

  function init(root) {
    if (root.dataset.bundleInitialized === 'true') return;
    root.dataset.bundleInitialized = 'true';

    const mainVariants = parseJSON(root.querySelector('[data-current-product-variants]'));
    const cards = [...root.querySelectorAll(SELECTORS.card)];
    const form = root.closest('form[action*="/cart/add"]') || findProductForm(root, mainVariants);

    cards.forEach((card) => {
      card.bundleVariants = parseJSON(card.querySelector('[data-product-variants]'));
      card.querySelectorAll(SELECTORS.select).forEach((select) => {
        select.addEventListener('change', () => updateCard(root, card));
      });
      card.querySelector(SELECTORS.toggle)?.addEventListener('click', () => toggleCard(root, card));
      updateCard(root, card);
    });

    if (!form || form.dataset.bundleSubmitBound === 'true') return;
    form.dataset.bundleSubmitBound = 'true';
    form.addEventListener('submit', () => prepareBundleSubmission(root, form, cards, mainVariants), true);
  }

  function findProductForm(root, variants) {
    const variantIds = new Set(variants.map((variant) => String(variant.id)));
    const scope = root.closest('product-info, .product, [data-product-root]') || document;
    return [...scope.querySelectorAll('form[action*="/cart/add"]')].find((form) => {
      const id = form.querySelector('[name="id"]')?.value;
      return id && variantIds.has(String(id));
    });
  }

  function updateCard(root, card) {
    const selects = [...card.querySelectorAll(SELECTORS.select)];
    const values = selects.map((select) => select.value);
    let variant = card.bundleVariants.find((item) => item.options.every((value, index) => value === values[index]));

    if (!variant?.available) {
      variant = card.bundleVariants.find((item) => item.available) || variant;
      if (variant?.available) selects.forEach((select, index) => { select.value = variant.options[index]; });
    }

    card.bundleVariant = variant || null;
    updateOptionAvailability(card);

    const purchasable = Boolean(variant?.available);
    const checkbox = card.querySelector(SELECTORS.checkbox);
    const toggle = card.querySelector(SELECTORS.toggle);
    const price = card.querySelector('[data-variant-price]');
    card.classList.toggle('bundle-card--unavailable', !purchasable);
    if (price && variant) price.textContent = formatMoney(variant.price, root.dataset.moneyFormat);
    if (checkbox) {
      checkbox.disabled = !purchasable;
      if (!purchasable) checkbox.checked = false;
    }
    if (toggle) toggle.disabled = !purchasable;
    updateToggle(root, card);
  }

  function updateOptionAvailability(card) {
    const selects = [...card.querySelectorAll(SELECTORS.select)];
    selects.forEach((select, targetIndex) => {
      [...select.options].forEach((option) => {
        option.disabled = !card.bundleVariants.some((variant) => {
          if (!variant.available || variant.options[targetIndex] !== option.value) return false;
          return selects.every((otherSelect, index) => index === targetIndex || variant.options[index] === otherSelect.value);
        });
      });
    });
  }

  function toggleCard(root, card) {
    const checkbox = card.querySelector(SELECTORS.checkbox);
    if (!checkbox || checkbox.disabled) return;
    checkbox.checked = !checkbox.checked;
    updateToggle(root, card);
  }

  function updateToggle(root, card) {
    const checkbox = card.querySelector(SELECTORS.checkbox);
    const button = card.querySelector(SELECTORS.toggle);
    if (!checkbox || !button) return;
    const selected = checkbox.checked && !checkbox.disabled;
    button.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', String(selected));
    button.querySelector('[data-toggle-label]').textContent = checkbox.disabled
      ? root.dataset.textSoldOut
      : selected ? root.dataset.textAdded : root.dataset.textAdd;
    button.querySelector('[data-toggle-symbol]').textContent = checkbox.disabled ? '×' : selected ? '✓' : '+';
  }

  function prepareBundleSubmission(root, form, cards, variants) {
    form.querySelectorAll('[data-bundle-generated]').forEach((input) => input.remove());
    const selectedCards = cards.filter((card) => card.querySelector(SELECTORS.checkbox)?.checked && card.bundleVariant?.available);
    if (selectedCards.length === 0) return;

    const idInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const currentVariant = variants.find((variant) => String(variant.id) === String(idInput?.value));
    if (!idInput || !currentVariant?.available) return;

    const items = [
      { id: currentVariant.id, quantity: Math.max(1, Number(quantityInput?.value) || 1) },
      ...selectedCards.map((card) => ({ id: card.bundleVariant.id, quantity: 1 }))
    ];
    items.forEach((item, index) => {
      appendInput(form, `items[${index}][id]`, item.id);
      appendInput(form, `items[${index}][quantity]`, item.quantity);
    });

    const sellingPlan = form.querySelector('[name="selling_plan"]');
    if (sellingPlan?.value) appendInput(form, 'items[0][selling_plan]', sellingPlan.value);

    idInput.disabled = true;
    if (quantityInput) quantityInput.disabled = true;
    window.setTimeout(() => {
      idInput.disabled = false;
      if (quantityInput) quantityInput.disabled = false;
      form.querySelectorAll('[data-bundle-generated]').forEach((input) => input.remove());
    }, 0);
  }

  function appendInput(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.bundleGenerated = '';
    form.appendChild(input);
  }

  function formatMoney(cents, format = '${{amount}}') {
    const placeholder = /\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return (Number(cents) / 100).toFixed(2);
    const options = match[1].includes('no_decimals')
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    const separators = match[1].includes('comma_separator') ? ['.', ','] : [',', '.'];
    let value = (Number(cents) / 100).toLocaleString('en-US', options);
    if (separators[0] === '.') value = value.replace(/,/g, '_').replace(/\./g, ',').replace(/_/g, '.');
    return format.replace(placeholder, value);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
